import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { AlertTriangle, Loader2, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UnclaimedAcknowledgementGate } from '@/components/unclaimed/UnclaimedAcknowledgementGate';
import {
  UnclaimedFiltersBar,
  UnclaimedFiltersTrigger,
  type UnclaimedFilters,
} from '@/components/unclaimed/UnclaimedFiltersBar';
import { UnclaimedInvoicesTable } from '@/components/unclaimed/UnclaimedInvoicesTable';
import { ClaimWizardDialog } from '@/components/unclaimed/ClaimWizardDialog';
import {
  clearUnclaimedClientCache,
  DEFAULT_UNCLAIMED_SORT,
  prefetchUnclaimedPageData,
  useUnclaimedAcknowledgement,
  useUnclaimedInvoices,
  type UnclaimedInvoiceRow,
  type UnclaimedSortBy,
  type UnclaimedSortDir,
} from '@/hooks/useUnclaimed';
import { useDebounce } from '@/hooks/use-debounce';
import { useAuth } from '@/context/AuthContext';
import { FINANCES_PATHS } from '@/lib/financesPaths';

/**
 * Spec — Unclaimed Commission.docx (User Story):
 *   "As an authorized advisor, I want to see commissions paid to TRAMS
 *    that aren't tied to anyone yet, and claim the ones that are mine."
 *
 * Page-level state:
 *   • acknowledgement gate (rendered until status.acknowledged === true)
 *   • table query state (page, pageSize, sortBy, sortDir, filters)
 *   • cross-page persistent selection — Map<compositeKey, row> where
 *     compositeKey = `${invoiceNo}-${bookingNo}-${agentBkgNo}`. The
 *     unclaimed pool stores multiple booking lines under each monthly
 *     TRAMS invoice, so `tramsInvoiceNo` alone is NOT row-unique.
 *   • wizard dialog with the rows being claimed
 */
const rowKey = (row: {
  tramsInvoiceNo: number;
  tramsBookingNo: number;
  tramsAgentBkgNo: number;
}): string =>
  `${row.tramsInvoiceNo}-${row.tramsBookingNo}-${row.tramsAgentBkgNo}`;

/**
 * `YYYY-MM-DD` strings sort lexicographically, so a string compare is
 * sufficient to detect inverted ranges. We strip BOTH endpoints when
 * inverted (rather than dropping just one) so the table doesn't silently
 * apply a half-open filter the user didn't intend.
 */
function sanitizeDateRanges(filters: UnclaimedFilters): UnclaimedFilters {
  const next: UnclaimedFilters = { ...filters };
  const pairs: Array<[keyof UnclaimedFilters, keyof UnclaimedFilters]> = [
    ['departureDateFrom', 'departureDateTo'],
    ['returnDateFrom', 'returnDateTo'],
    ['issueDateFrom', 'issueDateTo'],
  ];
  for (const [from, to] of pairs) {
    const fromVal = next[from];
    const toVal = next[to];
    if (fromVal && toVal && fromVal > toVal) {
      delete next[from];
      delete next[to];
    }
  }
  return next;
}
export default function UnclaimedCommissions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const ack = useUnclaimedAcknowledgement();
  const [, navigate] = useLocation();
  const prevUserIdRef = useRef<number | null>(null);

  // --- Table state ---
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Default sort by Departure Date (desc = soonest-past first). Was
  // `vendorPaidDate` but that column is no longer surfaced in the table —
  // see UnclaimedInvoicesTable.tsx COLUMNS comment.
  const [sortBy, setSortBy] = useState<UnclaimedSortBy>(DEFAULT_UNCLAIMED_SORT.sortBy);
  const [sortDir, setSortDir] = useState<UnclaimedSortDir>(DEFAULT_UNCLAIMED_SORT.sortDir);
  const [filters, setFilters] = useState<UnclaimedFilters>({});

  // --- Persistent selection across pages/filters ---
  // We retain the full row data alongside the composite key so the wizard
  // can submit without re-fetching, and so we keep showing "N selected"
  // badges even when the row scrolls out of view. Keyed by composite
  // (invoice, booking, agentBkg) — see file-level comment.
  const [selectedRows, setSelectedRows] = useState<
    Map<string, UnclaimedInvoiceRow>
  >(new Map());

  // --- Wizard ---
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardRows, setWizardRows] = useState<UnclaimedInvoiceRow[]>([]);

  const resetLocalUnclaimedState = useCallback(() => {
    setSelectedRows(new Map());
    setWizardOpen(false);
    setWizardRows([]);
    setPage(1);
    setFilters({});
  }, []);

  useEffect(() => {
    const nextId = user?.id ?? null;
    const prevId = prevUserIdRef.current;
    if (prevId != null && nextId != null && prevId !== nextId) {
      clearUnclaimedClientCache(queryClient);
      resetLocalUnclaimedState();
    }
    prevUserIdRef.current = nextId;
  }, [user?.id, queryClient, resetLocalUnclaimedState]);

  useEffect(() => {
    if (ack.data?.acknowledged) {
      void prefetchUnclaimedPageData(queryClient);
    }
  }, [ack.data?.acknowledged, queryClient]);

  // Debounce the filter object so each keystroke in the text inputs doesn't
  // fire its own Lambda call. The visible inputs stay controlled by `filters`
  // (instant feedback); the query key only updates after the user pauses
  // typing for 300 ms. Date pickers also flow through this — fine, since they
  // emit one discrete change per click.
  const debouncedFilters = useDebounce(filters, 300);

  // If a date range comes through inverted (from > to), drop both endpoints
  // before they hit the server. The filter bar shows a visible error on the
  // inputs so the user knows why; meanwhile the previous valid result set
  // stays on screen instead of collapsing to "0 results" on a logically
  // impossible range.
  const safeFilters = useMemo(
    () => sanitizeDateRanges(debouncedFilters),
    [debouncedFilters],
  );

  const list = useUnclaimedInvoices(
    {
      page,
      pageSize,
      sortBy,
      sortDir,
      ...safeFilters,
    },
    ack.data?.acknowledged === true,
  );

  const selectedKeys = useMemo(
    () => new Set(selectedRows.keys()),
    [selectedRows],
  );

  const handleToggleSelected = useCallback((row: UnclaimedInvoiceRow, on: boolean) => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      const key = rowKey(row);
      if (on) next.set(key, row);
      else next.delete(key);
      return next;
    });
  }, []);

  const handleSortChange = useCallback(
    (nextSortBy: UnclaimedSortBy, nextSortDir: UnclaimedSortDir) => {
      setSortBy(nextSortBy);
      setSortDir(nextSortDir);
      setPage(1);
    },
    [],
  );

  const handleFiltersChange = useCallback((next: UnclaimedFilters) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleClaimRow = (row: UnclaimedInvoiceRow) => {
    setWizardRows([row]);
    setWizardOpen(true);
  };

  const handleClaimSelected = () => {
    if (selectedRows.size === 0) return;
    setWizardRows(Array.from(selectedRows.values()));
    setWizardOpen(true);
  };

  /**
   * Fires once per successfully submitted row from the wizard. Drop the
   * row from the persistent selection IMMEDIATELY so:
   *   1) the "N selected" badge updates after each submission, not only
   *      at full completion;
   *   2) if the user closes the wizard mid-flow and re-opens it, the
   *      wizard receives a FRESH `wizardRows` (rebuilt from the live
   *      selection by `handleClaimSelected`) that excludes already-
   *      submitted rows — preventing the per-user dedup guard
   *      (`UNCLAIMED_ALREADY_CLAIMED_BY_USER`) from blocking a retry
   *      the user didn't intend.
   *
   * NOTE: we deliberately do NOT mutate `wizardRows` while the dialog
   * is open. The wizard tracks per-row submission state internally
   * (`submittedRowKeys`) and would reset its state if `rows` changed
   * identity mid-flow.
   */
  const handleRowComplete = useCallback((row: UnclaimedInvoiceRow) => {
    const key = rowKey(row);
    setSelectedRows((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleWizardComplete = () => {
    // Per-row removal is already handled by `handleRowComplete`. This
    // fires after the LAST pending row of the wizard run finishes; we
    // refresh the table data and (per spec) hop to the main invoice table.
    list.refetch();

    // Spec — Unclaimed Commission.docx (Claim Process → Acceptance Criteria):
    //   "After submitting a claim … user is redirected back to the main
    //    invoice table". The unclaimed page is *not* the main invoice
    //    table; the invoicing page is. Defer one tick so the toast can
    //    render before the route swap.
    setTimeout(() => navigate(FINANCES_PATHS.invoices), 400);
  };

  // --- Render ---

  if (ack.isLoading && !ack.data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (ack.error) {
    return (
      <div className="mx-auto max-w-md py-16 text-center text-sm text-rose-600">
        Could not load Unclaimed Commissions.
        <br />
        {ack.error instanceof Error ? ack.error.message : 'Please refresh.'}
      </div>
    );
  }

  if (!ack.data?.acknowledged) {
    return (
      <UnclaimedAcknowledgementGate
        status={ack.data!}
        onAcknowledged={() => ack.refetch()}
      />
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Unclaimed Commissions
          </h1>
          <p className="text-sm text-muted-foreground">
            Find and claim unassigned commissions.
          </p>
        </div>
        {/* AR-412: wraps on a phone — the nowrap date line + button row was
            wider than a 390px screen and forced sideways scrolling. */}
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          {list.data && (
            <p className="w-full text-sm text-muted-foreground sm:w-auto sm:whitespace-nowrap">
              Displaying invoices from{' '}
              {format(new Date(`${list.data.cutoffFrom}T00:00:00`), 'MMMM d, yyyy')} to{' '}
              {format(new Date(`${list.data.cutoffTo}T00:00:00`), 'MMMM d, yyyy')}.
            </p>
          )}
          {selectedRows.size > 0 && (
            <Badge variant="outline" className="text-sm">
              {selectedRows.size} selected
            </Badge>
          )}
          {/* AR-210 (TC-17.1, TC-29.2): the selection count was visual only. */}
          <span className="sr-only" role="status" aria-live="polite">
            {selectedRows.size ? `${selectedRows.size} selected` : ''}
          </span>
          <Button
            className="h-11 flex-1 sm:h-10 sm:flex-none"
            onClick={handleClaimSelected}
            disabled={selectedRows.size === 0}
          >
            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
            Claim selected invoices
            <span className="sr-only">{selectedRows.size ? `, ${selectedRows.size} selected` : ''}</span>
          </Button>
          <UnclaimedFiltersTrigger value={filters} onChange={handleFiltersChange} />
        </div>
      </div>

      <UnclaimedFiltersBar value={filters} onChange={handleFiltersChange} />

      {/* Spec — Report Page → Acceptance Criteria:
            "Display an error if there is an issue loading the unclaimed
             commissions." Surfaces both initial load failures and refetch
             failures (placeholderData keeps stale rows visible underneath). */}
      {list.error && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">
              Unable to load unclaimed commissions.
            </div>
            <div className="mt-0.5 text-xs opacity-90">
              {list.error instanceof Error
                ? list.error.message
                : 'Please try again.'}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            Retry
          </Button>
        </div>
      )}

      <UnclaimedInvoicesTable
        data={list.data}
        isLoading={list.isFetching}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortDir}
        selectedKeys={selectedKeys}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        onSortChange={handleSortChange}
        onToggleSelected={handleToggleSelected}
        onClaimRow={handleClaimRow}
      />

      <ClaimWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        rows={wizardRows}
        onRowComplete={handleRowComplete}
        onComplete={handleWizardComplete}
      />
    </div>
  );
}
